<template>
  <footer class="message-composer">
    <div class="message-composer__row" :class="{ 'message-composer__row--no-attach': !showAttach }">
      <button
        v-if="showAttach"
        class="message-composer__attach"
        type="button"
        aria-label="Add to chat"
        @click="$emit('open-actions')"
      >
        <i class="pi pi-plus" />
      </button>
      <input
        :value="draft"
        class="message-composer__input"
        :placeholder="placeholder"
        @input="onInput"
        @focus="$emit('focus')"
        @keydown.enter="onEnter"
      >
      <button
        class="message-composer__send"
        type="button"
        :disabled="!canSend"
        aria-label="Send"
        @click="$emit('send')"
      >
        <i class="pi pi-send" />
      </button>
    </div>
  </footer>
</template>

<script setup>
// The compose row, on every surface that has a chat.
//
// One row, one size. 44px is Apple's minimum for a control a thumb has to hit,
// and a pointer hits a 44px target at least as easily as a smaller one — so
// there is no device that needs this row smaller and nothing here for a surface
// to pick. The bottom padding clears the home indicator through `env()`, which
// resolves to nothing on a pointer surface and costs it nothing.
defineOptions({ name: 'MessageComposer' });

const props = defineProps({
  /** Whether the send button is armed — the page decides what "sendable" means. */
  canSend: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Message…' },
  // The `+` affordance. A surface with nothing to attach hides it, and the row
  // closes up rather than leaving a gap where it was.
  showAttach: { type: Boolean, default: true },
});

// The message draft. `:value` + `@input` rather than `v-model` on the input so
// the field stays a controlled one: the page writes the draft back — including
// the clear after a send — and the row never clears itself, because a send that
// failed must not lose what the user typed.
const draft = defineModel({ type: String, default: '' });

const emit = defineEmits(['send', 'focus', 'open-actions']);

function onInput(event) {
  draft.value = event.target.value;
}

// `canSend` gates the send whichever way the send was reached. The button can be
// disabled and the keyboard cannot, so this route needs the guard written out.
function onEnter() {
  if (props.canSend) emit('send');
}
</script>

<style scoped>
/* ── Composer ──────────────────────────────────────────────────────── */
.message-composer {
  /* The control size is a local custom property rather than four copies of one
     number — the row, the input and both buttons all measure against it. */
  --control-size: 44px;
  border-top: var(--p-message-composer-border-top-width) solid var(--p-message-composer-border-top-color);
  background: var(--p-message-composer-background);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  gap: var(--p-message-composer-gap);
  padding-top: var(--p-message-composer-padding-top);
  padding-inline: var(--p-message-composer-padding-inline);
  /* The floor is a brand decision and is a token; the inset is a device fact and
     is not — a theme has nothing to say about a home indicator. */
  padding-bottom: max(var(--p-message-composer-padding-bottom), env(safe-area-inset-bottom));
}

.message-composer__row {
  display: grid;
  grid-template-columns: var(--control-size) 1fr var(--control-size);
  gap: var(--p-message-composer-row-gap);
  align-items: center;
}

.message-composer__row--no-attach {
  grid-template-columns: 1fr var(--control-size);
}

.message-composer__attach,
.message-composer__send {
  width: var(--control-size);
  height: var(--control-size);
  border-radius: var(--p-message-composer-attach-border-radius);
  border: none;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.message-composer__attach {
  background: var(--p-message-composer-attach-background);
  color: var(--p-message-composer-attach-color);
  border: var(--p-message-composer-attach-border-width) solid var(--p-message-composer-attach-border-color);
}

.message-composer__attach i {
  font-size: var(--p-message-composer-attach-icon-font-size);
}

.message-composer__input {
  height: var(--control-size);
  min-width: 0;
  background: var(--p-message-composer-input-background);
  border: var(--p-message-composer-input-border-width) solid var(--p-message-composer-input-border-color);
  border-radius: var(--p-message-composer-input-border-radius);
  padding: var(--p-message-composer-input-padding);
  color: var(--p-message-composer-input-color);
  font-family: inherit;
  font-size: var(--p-message-composer-input-font-size);
  outline: none;
}

.message-composer__input::placeholder {
  color: var(--p-message-composer-input-placeholder-color);
}

.message-composer__input:focus {
  border-color: var(--p-message-composer-input-focus-border-color);
}

.message-composer__send {
  background: var(--p-message-composer-send-background);
  color: var(--p-message-composer-send-color);
}

.message-composer__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.message-composer__send i {
  font-size: var(--p-message-composer-send-icon-font-size);
}
</style>
